import { API_BASES } from '@/constant/endpoint.constant';
import { clients } from '@/lib/https';
import { AI_ENDPOINTS } from '@/modules/gpt-chats/constants/endpoint.constant';
import type {
  IAddAPIToolPayload,
  IAddAPIToolResponse,
  IAddMCPServerPayload,
  IAddMCPServerResponse,
  IAddOpenToolResponse,
  ICreateApiActionPayload,
  ICreateApiActionResponse,
  IDeleteToolResponse,
  IGetOpenToolsPayload,
  IGetOpenToolsResponse,
  IGetToolSchemaResponse,
  IGetToolsPayload,
  IGetToolsResponse,
  IMCPServerSetupPayload,
  IMCPServerSetupResponse,
  ISaveAuthConfigPayload,
  ISaveAuthConfigResponse,
  ISaveTutorialPayload,
  ITestApiActionPayload,
  ITestApiActionResponse,
  IToolAssociatedAgentCountResponse,
  IToolDetails,
  IToolSummaryPayload,
  IToolSummaryResponse,
  IUpdateAPIToolPayload,
  IUpdateAPIToolResponse,
  IUpdateMCPServerPayload,
  IUpdateMCPServerResponse,
} from '@/modules/gpt-chats/types/tools.service.type';

const withoutProjectKey = <T extends Record<string, unknown>>(
  payload: T
): Omit<T, 'project_key'> => {
  const { ...rest } = payload;
  return rest;
};

export class ToolsService {
  getTools(payload: IGetToolsPayload): Promise<IGetToolsResponse> {
    const { tool_type, tool_status, page = 1, page_size = 50 } = payload;

    const params = new URLSearchParams({
      page: String(page),
      page_size: String(page_size),
    });

    if (tool_type) {
      params.append('tool_type', tool_type);
    }

    if (tool_status) {
      params.append('tool_status', tool_status);
    }

    return clients.get(`${API_BASES.AI}${AI_ENDPOINTS.TOOLS}/?${params.toString()}`);
  }

  addMCPServer(payload: IAddMCPServerPayload): Promise<IAddMCPServerResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_MCP_SERVER}`,
      JSON.stringify(withoutProjectKey(payload as unknown as Record<string, unknown>))
    );
  }

  addAPITool(payload: IAddAPIToolPayload): Promise<IAddAPIToolResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_API}`,
      JSON.stringify(withoutProjectKey(payload as unknown as Record<string, unknown>))
    );
  }

  getToolDetails(toolId: string, isBlocksTool?: boolean): Promise<IToolDetails> {
    const params = new URLSearchParams();
    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    return clients.get(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_BY_ID.replace(':id', toolId)}?${params.toString()}`
    );
  }

  getOpenToolDetails(toolId: string): Promise<IToolDetails> {
    return clients.get(`${API_BASES.AI}/tools/defaults/${toolId}`);
  }

  updateMCPServer(payload: IUpdateMCPServerPayload): Promise<IUpdateMCPServerResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_MCP_SERVER}`,
      JSON.stringify({
        name: payload.name,
        description: payload.description,
        id: payload.tool_id,
        is_open_tool: payload.is_open_tool,
        is_blocks_authenticated: payload.is_blocks_authenticated,
        status: payload.status,
      })
    );
  }

  updateAPITool(payload: IUpdateAPIToolPayload): Promise<IUpdateAPIToolResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_API}`,
      JSON.stringify({
        name: payload.name,
        description: payload.description,
        base_url: payload.base_url,
        id: payload.tool_id,
        is_open_tool: payload.is_open_tool,
        is_blocks_authenticated: payload.is_blocks_authenticated,
        status: payload.status,
      })
    );
  }

  deleteTool(toolId: string, isBlocksTool?: boolean): Promise<IDeleteToolResponse> {
    const params = new URLSearchParams();
    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    return clients.delete(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_BY_ID.replace(':id', toolId)}?${params.toString()}`
    );
  }

  saveAuthConfig(
    toolId: string,
    payload: ISaveAuthConfigPayload
  ): Promise<ISaveAuthConfigResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_API_AUTH_CONFIG.replace(':id', toolId)}`,
      JSON.stringify(withoutProjectKey(payload as unknown as Record<string, unknown>))
    );
  }

  createApiAction(
    toolId: string,
    payload: ICreateApiActionPayload,
    actionId?: string
  ): Promise<ICreateApiActionResponse> {
    const requestPayload = {
      ...withoutProjectKey(payload as unknown as Record<string, unknown>),
      ...(actionId && { actionId: actionId }),
    };
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_API_ACTION.replace(':id', toolId)}`,
      JSON.stringify(requestPayload)
    );
  }

  deleteToolApiAction(
    tool_id: string,
    action_id: string,
    isBlocksTool?: boolean
  ): Promise<IDeleteToolResponse> {
    const params = new URLSearchParams();
    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    const queryString = params.toString();
    return clients.delete(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_DELETE_ACTION.replace(':id', tool_id).replace(':actionId', action_id)}${
        queryString ? `?${queryString}` : ''
      }`
    );
  }

  saveTutorial(
    toolId: string,
    payload: ISaveTutorialPayload
  ): Promise<{ is_success: boolean; message?: string }> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_TUTORIAL.replace(':id', toolId)}`,
      JSON.stringify({
        ...payload,
      })
    );
  }

  setupMCPServer(
    toolId: string,
    payload: IMCPServerSetupPayload
  ): Promise<IMCPServerSetupResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_MCP_SERVER_SETUP.replace(':id', toolId)}`,
      JSON.stringify(withoutProjectKey(payload as unknown as Record<string, unknown>))
    );
  }

  refreshMCPServer(
    toolId: string,
    isBlocksTool?: boolean
  ): Promise<{
    is_success: boolean;
    item_id: string;
    detail: string;
    error?: Record<string, unknown>;
  }> {
    const params = new URLSearchParams();
    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_REFRESH_MCP.replace(':id', toolId)}?${params.toString()}`,
      JSON.stringify({})
    );
  }

  testApiAction(
    toolId: string,
    actionId: string,
    payload: ITestApiActionPayload
  ): Promise<ITestApiActionResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_TEST_ACTION.replace(':id', toolId).replace(':actionId', actionId)}`,
      JSON.stringify(withoutProjectKey(payload as unknown as Record<string, unknown>))
    );
  }

  getToolSchema(toolId: string, isBlocksTool?: boolean): Promise<IGetToolSchemaResponse> {
    const params = new URLSearchParams();
    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    return clients.get(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_SCHEMA_OPENAPI.replace(':id', toolId)}?${params.toString()}`
    );
  }

  testMcpAuth(
    toolId: string,
    payload: { auth_config: Record<string, unknown> },
    isBlocksTool?: boolean
  ): Promise<{ is_success: boolean; message?: string }> {
    const params = new URLSearchParams({});
    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_TEST_MCP_AUTH.replace(':id', toolId)}?${params.toString()}`,
      JSON.stringify(payload)
    );
  }

  initiateOAuth(
    toolId: string,
    payload: { oauth_config: Record<string, unknown> },
    isBlocksTool?: boolean
  ): Promise<{ is_success: boolean; authorization_url?: string; message?: string }> {
    const params = new URLSearchParams();
    if (typeof isBlocksTool !== 'undefined') {
      params.append('is_open_tool', String(isBlocksTool));
    }

    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_INITIATE_OAUTH.replace(':id', toolId)}?${params.toString()}`,
      JSON.stringify(payload)
    );
  }

  completeOAuth(
    toolId: string,
    payload: { code: string; state: string; project_key?: string; is_open_tool?: boolean }
  ): Promise<{ is_success: boolean; message?: string; data?: Record<string, unknown> }> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_COMPLETE_OAUTH.replace(':id', toolId)}`,
      JSON.stringify(withoutProjectKey(payload))
    );
  }

  getToolSummary(payload: IToolSummaryPayload): Promise<IToolSummaryResponse> {
    return clients.get(
      `${API_BASES.AI}${AI_ENDPOINTS.TOOL_SUMMARY.replace(':id', payload.tool_id)}?${payload.agent_id ? `&agent_id=${payload.agent_id}` : ''}${payload.action_id ? `&action_id=${payload.action_id}` : ''}&start_date=${payload.start_date}&end_date=${payload.end_date}`
    );
  }

  getToolAssociatedAgentCount(toolId: string): Promise<IToolAssociatedAgentCountResponse> {
    return clients.get(`${API_BASES.AI}${AI_ENDPOINTS.TOOL_AGENT_COUNT.replace(':id', toolId)}`);
  }

  getOpenTools(payload: IGetOpenToolsPayload): Promise<IGetOpenToolsResponse> {
    const params = new URLSearchParams();
    if (payload.tool_type) params.append('tool_type', payload.tool_type);
    if (payload.tool_status) params.append('tool_status', payload.tool_status);
    params.append('page', payload.page.toString());
    params.append('page_size', payload.page_size.toString());

    return clients.get(`${API_BASES.AI}/tools/defaults/list?${params.toString()}`);
  }

  addOpenTool(toolId: string): Promise<IAddOpenToolResponse> {
    return clients.post(`${API_BASES.AI}/tools/copy-from-default/${toolId}`, JSON.stringify({}));
  }
}

export const toolsService = new ToolsService();
